/**
 * Flow Optimizer — config query/mutation hooks
 * For Media Devoted | Created By Ciro Autiero
 *
 * Provider-aware: pass `provider` to dispatch to /voluum/* or /binom/*.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FlowOptimizerConfig } from '@trafficpulse/shared';

import {
  getConfig,
  getMetadata,
  setKillSwitch,
  upsertConfig,
  type FlowOptimizerProvider,
} from '../services/flow-optimizer-api';
import { queueProviderOf } from '@/features/trackers/flow-optimizer/queue-provider';
import { useDrawerProfile } from '@/features/flow-optimizer/tracker/flow-tracker-context';
import { queueKeys } from './useFlowOptimizerQueue';

/** Exported for cross-feature cache writes (library package apply). */
export const QK = {
  config: (provider: FlowOptimizerProvider, campaignId: string) =>
    [provider, 'flow-optimizer', 'config', campaignId] as const,
  metadata: (provider: FlowOptimizerProvider) =>
    [provider, 'flow-optimizer', 'metadata'] as const,
};

export function useFlowOptimizerConfig(
  campaignId: string,
  flowId?: string,
  provider: FlowOptimizerProvider = 'voluum',
) {
  return useQuery({
    queryKey: QK.config(provider, campaignId),
    queryFn: () => getConfig(campaignId, flowId, provider),
    enabled: Boolean(campaignId),
    staleTime: 30_000,
  });
}

/** Without `provider`: the drawer's tracker (its profile's metadataFrom),
 *  'voluum' outside any drawer — Voluum and Binom keep today's request.
 *  `enabled: false` skips the request (the Automation's own metric catalog). */
export function useFlowOptimizerMetadata(
  provider?: FlowOptimizerProvider,
  opts: { enabled?: boolean } = {},
) {
  const scoped = useDrawerProfile().metadataFrom;
  const source = provider ?? scoped;
  return useQuery({
    queryKey: QK.metadata(source),
    queryFn: () => getMetadata(source),
    staleTime: 5 * 60_000,
    enabled: opts.enabled ?? true,
  });
}

export function useUpsertFlowOptimizerConfig(
  campaignId: string,
  provider: FlowOptimizerProvider = 'voluum',
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<FlowOptimizerConfig> & { flowId: string }) =>
      upsertConfig(campaignId, body, provider),
    onSuccess: (data) => {
      queryClient.setQueryData(QK.config(provider, campaignId), data);
      // The QUEUE is computed server-side FROM this config: pool caps, the
      // over-capacity plan, what a bulk promote says will fit. Saving used to
      // update only the config cache, so the listing kept serving the old caps
      // until something else happened to refetch it — remove a cap and the
      // promote dialog still refused with "every pool is at capacity". The
      // pinned rotation preview re-runs off this too (use-live-preview-refresh
      // keys on the queue's dataUpdatedAt), so one invalidation covers both.
      // A tracker without a queue (Tres Commas) has nothing to invalidate.
      const queue = queueProviderOf(provider);
      if (!queue) return;
      void queryClient.invalidateQueries({
        queryKey: queueKeys.queue(queue, campaignId),
      });
    },
  });
}

export function useSetKillSwitch(
  campaignId: string,
  provider: FlowOptimizerProvider = 'voluum',
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (killSwitch: boolean) =>
      setKillSwitch(campaignId, killSwitch, provider),
    onSuccess: (data) => {
      queryClient.setQueryData(QK.config(provider, campaignId), data);
      void queryClient.invalidateQueries({
        queryKey: ['flow-overlay-config', provider, campaignId],
      });
    },
  });
}
